import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import {
  ActionPerformed,
  PushNotificationSchema,
  PushNotifications,
  Token,
} from '@capacitor/push-notifications';
import { DateInterval } from 'src/app/model/date-interval.model';
import { Observable } from 'rxjs';
import { createAuthorizationHeader } from 'src/app/utils/headers';
import { GlobalService } from 'src/app/global.config';
import { HttpClient } from '@angular/common/http';
import { SigninService } from 'src/app/signin/signin.service';
import { TokenLutDto } from 'src/app/model/data-managemnt';
import { User } from 'src/app/signin/credentials';
import { PushPlatform, WebPushService } from 'src/app/utils/push/web-push.service';

@Injectable({
  providedIn: 'root'
})
export class AlertManagementService {
  private pushListenersReady = false;
  private pushRegistering = false;

  constructor(
    private signinService: SigninService,
    private _http: HttpClient,
    private globalService: GlobalService,
    private webPushService: WebPushService
  ) {}

  getNotifications(dateInterval: DateInterval): Observable<any> {
    let headers = createAuthorizationHeader();
    return this._http.post(
      this.globalService.dns +
        'alerts/notifications/' +
        this.signinService.isRootAdmin() +
        '/' +
        this.signinService.getUserId(),
      dateInterval,
      { headers: headers }
    );
  }

  add(tokenLutDto: TokenLutDto) {
    let headers = createAuthorizationHeader();
    return this._http.post(this.globalService.dns + 'tokenLut/update', tokenLutDto, {
      headers: headers,
    });
  }

  /**
   * Register FCM token (Android / iOS native, or browser Web Push) and upsert token_lut.
   * Call after login (when id_token + currentUser are saved).
   */
  async registerPushToken(user?: User | null, onPushReceived?: () => void): Promise<void> {
    const currentUser = user || this.readCurrentUser();
    if (!currentUser?.username) {
      console.warn('Cannot register push token: user is not signed in');
      return;
    }

    if (this.pushRegistering) {
      return;
    }
    this.pushRegistering = true;

    try {
      if (Capacitor.isNativePlatform()) {
        await this.registerNativePushToken(currentUser, onPushReceived);
      } else {
        await this.registerWebPushToken(currentUser, onPushReceived);
      }
    } catch (err) {
      console.error('Error requesting/registering push notifications:', err);
    } finally {
      this.pushRegistering = false;
    }
  }

  private async registerNativePushToken(
    currentUser: User,
    onPushReceived?: () => void
  ): Promise<void> {
    let deviceId = '';
    let language = localStorage.getItem('lang') || 'fr';
    const platform = this.resolveNativePlatform();

    try {
      const idInfo = await Device.getId();
      deviceId = idInfo.identifier || '';
      const langInfo = await Device.getLanguageCode();
      language = localStorage.getItem('lang') || langInfo.value || 'fr';
    } catch (error) {
      console.error('Error getting device info:', error);
    }

    if (!deviceId) {
      console.warn('Cannot register push token: unique device id is missing');
      return;
    }

    await this.ensureNativePushListeners(
      deviceId,
      currentUser.username,
      language,
      platform,
      onPushReceived
    );

    const permission = await PushNotifications.requestPermissions();
    if (permission.receive !== 'granted') {
      console.warn('Push notification permission not granted:', permission.receive);
      return;
    }

    await PushNotifications.register();
  }

  private async registerWebPushToken(
    currentUser: User,
    onPushReceived?: () => void
  ): Promise<void> {
    const language = localStorage.getItem('lang') || 'fr';
    const deviceId = this.webPushService.resolveBrowserDeviceId();

    if (!this.webPushService.isConfigured()) {
      console.error(
        '[WebPush] tokenLut/update skipped: active profile has no firebase web config (vapidKey/appId). ' +
          'Add firebase: { ... } on the profile in global.config.ts.'
      );
      return;
    }

    const token = await this.webPushService.requestPermissionAndToken((payload) => {
      console.log('[WebPush] Foreground push received', payload);
      if (onPushReceived) {
        onPushReceived();
      }
    });

    if (!token) {
      console.error('[WebPush] No FCM web token obtained; tokenLut/update was not called');
      return;
    }

    console.log('[WebPush] Publishing token to tokenLut/update', { deviceId, platform: 'web' });
    this.publishTokenLut({
      deviceid: deviceId,
      username: currentUser.username,
      token,
      language,
      lastTime: new Date(),
      platform: 'web',
    });
  }

  private resolveNativePlatform(): PushPlatform {
    const platform = Capacitor.getPlatform();
    if (platform === 'ios') {
      return 'ios';
    }
    return 'android';
  }

  private readCurrentUser(): User | null {
    try {
      return JSON.parse(localStorage.getItem('currentUser') || 'null');
    } catch {
      return null;
    }
  }

  private publishTokenLut(tokenLutDto: TokenLutDto): void {
    this.add(tokenLutDto).subscribe({
      next: () =>
        console.log(
          `FCM token published to token_lut (platform=${tokenLutDto.platform || 'unknown'})`
        ),
      error: (err) => console.error('Error publishing FCM token to token_lut:', err),
    });
  }

  private async ensureNativePushListeners(
    deviceId: string,
    username: string,
    language: string,
    platform: PushPlatform,
    onPushReceived?: () => void
  ): Promise<void> {
    try {
      await PushNotifications.removeAllListeners();
    } catch {
      // Ignore if no listeners were registered yet.
    }
    this.pushListenersReady = false;

    await PushNotifications.addListener('registration', (token: Token) => {
      console.log(
        `Push registration success for device=${deviceId} user=${username} platform=${platform} lang=${language}`
      );

      this.publishTokenLut({
        deviceid: deviceId,
        username,
        token: token.value,
        language: language || 'fr',
        lastTime: new Date(),
        platform,
      });
    });

    await PushNotifications.addListener('registrationError', (error: any) => {
      console.error('FCM registration error:', JSON.stringify(error));
    });

    await PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        console.log('Push received:', JSON.stringify(notification));
        if (onPushReceived) {
          onPushReceived();
        }
      }
    );

    await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (notification: ActionPerformed) => {
        console.log('Push action performed:', JSON.stringify(notification));
      }
    );

    this.pushListenersReady = true;
  }

  getNotificationsByIdDeviceType(
    idDevice: number,
    type: string,
    dateInterval: DateInterval
  ): Observable<any> {
    let headers = createAuthorizationHeader();
    return this._http.post(
      this.globalService.dns + 'alerts/notificationsdetails/' + idDevice,
      { dateInterval, type },
      { headers: headers }
    );
  }

  findVehicleByDeviceId(deviceId: number) {}

  getEventsByDeviceAndType(deviceId, type) {}
}
